import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DocumentTemplateService } from '../document-template.service';
import Swal from 'sweetalert2';
import { helper } from 'src/environments/helper';

export interface CloneTemplateDialogData {
    templateId: number;
    templateType: 'oficial' | 'cerere' | 'smart_group';
    templateName: string;
}

@Component({
    selector: 'app-clone-template-dialog',
    templateUrl: './clone-template-dialog.component.html',
    styleUrls: ['./clone-template-dialog.component.scss']
})
export class CloneTemplateDialogComponent implements OnInit {
    helper = helper;

    // Dialog state
    isCloning = false;
    templatePreview: any = null;
    availableGroups: any[] = [];

    // Form data
    cloneType: 'document' | 'smart_group' | 'smart_part' = 'document';
    newTemplateName: string = '';
    targetGroupId: number | null = null;
    partDescription: string = '';

    // Cloning options
    includeSignatures: boolean = true;
    includeCategories: boolean = true;
    // includeParameters: boolean = true;
    includeParts: boolean = true;

    constructor(
        public dialogRef: MatDialogRef<CloneTemplateDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: CloneTemplateDialogData,
        private templateService: DocumentTemplateService
    ) {
        // Set default clone type based on source template type
        if (data.templateType === 'smart_group') {
            this.cloneType = 'smart_group';
        }

        // Set default name
        this.newTemplateName = `${data.templateName} - Copie`;
    }

    ngOnInit(): void {
        this.loadTemplatePreview();
    }

    /**
     * Load template preview information
     */
    loadTemplatePreview(): void {
        this.templateService.getClonePreview({
            template_id: this.data.templateId,
            template_type: this.data.templateType
        }).subscribe(
            (response: any) => {
                if (response.success && response.data) {
                    this.templatePreview = response.data;
                }
            },
            (error) => {
                console.error('Error loading template preview:', error);
                Swal.fire({
                    title: this.helper.dialogConfig.headers.errorAtentie,
                    text: 'Nu am putut încărca informațiile templateului.',
                    icon: 'error',
                    confirmButtonText: this.helper.dialogConfig.buttonLabels.close,
                });
            }
        );
    }

    /**
     * Handle clone type change
     */
    onCloneTypeChange(): void {
        // Reset type-specific fields
        this.targetGroupId = null;
        this.partDescription = '';

        // Adjust default options based on clone type
        if (this.cloneType === 'smart_part') {
            this.includeSignatures = false;
            this.includeCategories = false;
        } else {
            this.includeSignatures = true;
            this.includeCategories = true;
        }
    }

    /**
     * Validate form before cloning
     */
    isFormValid(): boolean {
        if (!this.newTemplateName.trim()) {
            return false;
        }

        if (this.cloneType === 'smart_part' && !this.targetGroupId) {
            return false;
        }

        return true;
    }

    /**
     * Execute the cloning operation
     */
    onClone(): void {
        if (!this.isFormValid()) {
            return;
        }

        this.isCloning = true;

        // Prepare clone request based on type
        if (this.cloneType === 'document') {
            this.cloneDocumentTemplate();
        }
    }

    /**
     * Clone as document template
     */
    private cloneDocumentTemplate(): void {
        const cloneData = {
            name: this.newTemplateName.trim(),
            include_signatures: this.includeSignatures,
            include_categories: this.includeCategories,
            // include_parameters: this.includeParameters
        };

        this.templateService.cloneDocumentTemplate(this.data.templateId, cloneData).subscribe(
            (response: any) => {
                this.dialogRef.close({ success: true, data: response.data });
            },
            (error) => {
                this.handleCloneError(error);
            }
        );
    }

    /**
     * Handle successful cloning
     */

    /**
     * Handle cloning error
     */
    private handleCloneError(error: any): void {
        this.isCloning = false;

        let errorMessage = 'A apărut o eroare în timpul clonării.';
        if (error.error && error.error.message) {
            errorMessage = error.error.message;
        }

        Swal.fire({
            title: this.helper.dialogConfig.headers.errorAtentie,
            text: errorMessage,
            icon: 'error',
            confirmButtonText: this.helper.dialogConfig.buttonLabels.close,
        });
    }

    /**
     * Cancel dialog
     */
    onCancel(): void {
        this.dialogRef.close({ success: false });
    }
}